import type { CalendarRecurrence } from '@frontend/api-types';
import { debounce, deserializeJson, serializeJson } from '@frontend/duck-tape';
import { useAuthSlice } from '@frontend/redux';
import type { SocketMethod } from '@frontend/sockets';
import { Button, Emoji, FlatList, Header, Image, PageContainer, Text, XStack, YStack } from '@frontend/web-react';
import { safeCss } from '@frontend/web-utils';
import {
  useDismissAllCalendarRecurrencesMutation,
  useDismissCalendarRecurrenceMutation,
  useListCalendarRecurrencesQuery,
} from '@frontend/web/hooks';
import { disconnectSocket, getSocket, initializeSocket } from '@frontend/web/utils';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { NoCalendarRecurrencesIllustration } from '@frontend/assets';
import { useFeatureIsOn } from '@frontend/react';

const DISMISS_ALL_FADE_DURATION = 1000;
const DISMISS_INDIVIDUAL_FADE_DURATION = 500;

export type ExtraDataType = {
  animateDismissAll: boolean;
  description: string;
  fadingOutIds: Set<string>;
  firstVisibleIndex: number;
  isStreaming: boolean;
  otherText: string;
  selectedIndex: number | null;
  taskSuggestions: string[];
};

const CalendarRecurrences = () => {
  const { enabled: isDiscoverTabEnabled } = useFeatureIsOn('is_discover_tab_enabled');
  const router = useRouter();
  const {
    data: calendarRecurrences = [],
    isLoading: isListCalendarRecurrencesLoading,
    refetch: refetchCalendarRecurrences,
  } = useListCalendarRecurrencesQuery(undefined, { refetchOnMountOrArgChange: true });
  const [dismissCalendarRecurrence] = useDismissCalendarRecurrenceMutation();
  const [dismissAllCalendarRecurrence] = useDismissAllCalendarRecurrencesMutation();
  const { token } = useAuthSlice();
  // const { openCreateTaskModal } = useCreateTaskContext();
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());
  const [extraData, setExtraData] = useState<ExtraDataType>({
    animateDismissAll: false,
    description: '',
    fadingOutIds: new Set(),
    firstVisibleIndex: 0,
    isStreaming: false,
    otherText: '',
    selectedIndex: null,
    taskSuggestions: [],
  });
  const [isPageFadingOut, setIsPageFadingOut] = useState(false);

  const filteredCalendarRecurrences = useMemo(
    () => calendarRecurrences.filter((c) => !hiddenIds.has(c.id)),
    [calendarRecurrences, hiddenIds],
  );

  useEffect(() => {
    if (!isDiscoverTabEnabled) {
      router.navigate({ to: '/app/home' });
    }
  }, [isDiscoverTabEnabled]);

  const handleOutsidePress = useCallback(() => {
    setExtraData({
      animateDismissAll: false,
      description: '',
      fadingOutIds: new Set(),
      firstVisibleIndex: 0,
      isStreaming: false,
      otherText: '',
      selectedIndex: null,
      taskSuggestions: [],
    });
    disconnectSocket();
  }, []);

  const handleProcessCalendarRecurrence = useCallback(
    (calendarRecurrence: CalendarRecurrence, dismiss: boolean, created: boolean = false) => {
      if (dismiss) {
        setExtraData((prev) => ({
          ...prev,
          fadingOutIds: new Set(prev.fadingOutIds).add(calendarRecurrence.id),
        }));

        setTimeout(() => {
          dismissCalendarRecurrence(calendarRecurrence.id);
          setHiddenIds((prev) => new Set(prev).add(calendarRecurrence.id));

          setExtraData((prev) => ({
            ...prev,
            fadingOutIds: new Set(Array.from(prev.fadingOutIds).filter((id) => id !== calendarRecurrence.id)),
          }));
        }, DISMISS_INDIVIDUAL_FADE_DURATION);
      } else {
        handleOutsidePress();
      }

      if (created) {
        setHiddenIds((prev) => new Set(prev).add(calendarRecurrence.id));
        refetchCalendarRecurrences();
      }
    },
    [dismissCalendarRecurrence, handleOutsidePress],
  );

  const handleTaskSuggestionsUpdate = debounce<
    SocketMethod<'taskCreation', 'update_calendar_recurrence_task_suggestions'>
  >(
    (json) => {
      const data = deserializeJson(json);
      setExtraData((prev) => {
        return {
          ...prev,
          // if "Other" is present, it means we have already received the completed event and should re-add
          taskSuggestions:
            prev.taskSuggestions.indexOf('Other') !== -1 ? [...data.content.tasks, 'Other'] : data.content.tasks,
        };
      });
    },
    50,
    { maxWait: 100 },
  );

  const setupSocket = ({ calendarRecurrence, token }: { calendarRecurrence: CalendarRecurrence; token: string }) => {
    if (getSocket('taskCreation')) {
      disconnectSocket();
    }
    const socket = initializeSocket({
      channelType: 'taskCreation',
      token,
    });

    const onConnect = () => {
      socket.emit(
        'generate_calendar_recurrence_task_suggestions',
        serializeJson({ calendar_recurrence_id: calendarRecurrence.id }),
      );
      setExtraData((prev) => ({ ...prev, isStreaming: true }));
    };

    const onDisconnect = () => {
      setExtraData((prev) => ({ ...prev, isStreaming: false }));
    };

    const onCompleted = () => {
      disconnectSocket();
      setExtraData((prev) => {
        return {
          ...prev,
          isStreaming: false,
          taskSuggestions: [...prev.taskSuggestions, 'Other'],
        };
      });
    };

    const onFailed = () => {
      setExtraData((prev) => ({ ...prev, isStreaming: false }));
      disconnectSocket();
    };

    socket.on('update_calendar_recurrence_task_suggestions', handleTaskSuggestionsUpdate);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('calendar_recurrence_task_suggestions_complete', onCompleted);
    socket.on('calendar_recurrence_task_suggestions_failed', onFailed);
    return socket;
  };

  const handleItemPress = (index: number) => {
    if (extraData.selectedIndex !== null) {
      // if another item is already selected, unselect it
      disconnectSocket();
      setExtraData({
        animateDismissAll: false,
        description: '',
        fadingOutIds: new Set(),
        firstVisibleIndex: 0,
        isStreaming: false,
        otherText: '',
        selectedIndex: null,
        taskSuggestions: [],
      });
    } else {
      // if no item is selected, select the new item
      setExtraData((prev) => ({ ...prev, selectedIndex: index }));
    }
  };

  const handleDismissAll = () => {
    setIsPageFadingOut(true);

    setTimeout(() => {
      dismissAllCalendarRecurrence().then(() => {
        setIsPageFadingOut(false);
      });
    }, DISMISS_ALL_FADE_DURATION);
  };

  useEffect(() => {
    if (extraData.selectedIndex !== null && extraData.selectedIndex < filteredCalendarRecurrences.length && token) {
      setupSocket({ calendarRecurrence: filteredCalendarRecurrences[extraData.selectedIndex]!, token });
    }
  }, [extraData.selectedIndex]);

  const renderItem = useCallback(
    ({ item }: { index: number; item: CalendarRecurrence }) => {
      const isFadingOut = extraData.fadingOutIds.has(item.id);

      return (
        <div
          className={`transition-opacity ${'duration-' + DISMISS_INDIVIDUAL_FADE_DURATION} ${isFadingOut ? 'opacity-0' : 'opacity-100'}`}
          style={{ pointerEvents: isFadingOut ? 'none' : 'auto' }}
        >
          {/* <CalendarRecurrenceCard
            calendarRecurrence={item}
            extraData={extraData}
            handleProcessCalendarRecurrence={handleProcessCalendarRecurrence}
            handleSelectCalendarRecurrence={handleItemPress}
            index={index}
            openCreateTaskModal={openCreateTaskModal}
            setExtraData={setExtraData}
          /> */}
        </div>
      );
    },
    [extraData, handleProcessCalendarRecurrence, handleItemPress],
  );

  return (
    <PageContainer
      className={`flex-1 transition-opacity ${'duration-' + DISMISS_ALL_FADE_DURATION} ${isPageFadingOut ? 'opacity-0' : 'opacity-100'}`}
      isLoading={isListCalendarRecurrencesLoading}
      isScrollable={false}
      padding="none"
    >
      <YStack className="flex-1 overflow-y-auto">
        <Header
          className="bg-trueWhite top-none sticky mx-auto w-full max-w-screen-lg"
          onClickBack={router.history.back}
          rightContent={
            filteredCalendarRecurrences?.length ? (
              <Button
                disabled={isPageFadingOut}
                label="Dismiss All"
                onClick={handleDismissAll}
                size="sm"
                variant="secondary"
              />
            ) : null
          }
          title=""
        />
        <FlatList
          ListEmptyComponent={
            <YStack className="gap-lg m-auto size-full max-w-screen-lg items-center justify-center">
              <Image
                // eslint-disable-next-line tailwindcss/no-arbitrary-value
                className="h-[90px] w-[381px]"
                source={NoCalendarRecurrencesIllustration}
              />
              <YStack className="gap-sm items-center">
                <Text className="text-textPrimary" type="h5">
                  Good job ducking like a pro. <Emoji emoji=":duck:" />
                </Text>
                <Text className="text-textSecondary" type="p-sm">
                  This banner will pop up again when there are more repeated events to review. Until then, enjoy your
                  day!
                </Text>
              </YStack>
              <XStack>
                <Button
                  onClick={() => router.navigate({ search: { tab: 'scheduled' }, to: '/app/task' })}
                  rightIconName="IconCornerDownLeft"
                  size="sm"
                >
                  View all scheduled tasks
                </Button>
              </XStack>
            </YStack>
          }
          ListHeaderComponent={
            filteredCalendarRecurrences?.length ? (
              <YStack className="gap-sm p-md mx-auto w-full max-w-screen-lg items-start justify-start">
                <XStack className="gap-xs">
                  <Text className="text-textSecondary" type="h4">
                    Manage
                  </Text>
                  <Text className="text-textPrimary" type="h4">
                    {' '}
                    repeating events{' '}
                  </Text>
                  <Text className="text-textSecondary" type="h4">
                    effortlessly
                  </Text>
                </XStack>
                <Text className="text-textPrimary" type="p-sm">
                  Below are repeating events {"we've"} found on your calendar. Tap one to get started!
                </Text>
              </YStack>
            ) : null
          }
          accessibilityLabel="Calendar recurrences list"
          className="flex-1"
          contentContainerClassName={safeCss(
            'gap-md grid grid-cols-1 md:grid-cols-2 auto-rows-auto p-md max-w-screen-lg self-center w-full shrink',
          )}
          data={filteredCalendarRecurrences}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
        />
      </YStack>
    </PageContainer>
  );
};

export const Route = createFileRoute('/app/calendar-recurrence/')({
  component: CalendarRecurrences,
});
